import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
	getFormById,
	createFormSection,
	assignFieldToSection,
} from '../../store/formBuilder';
import Input from '../../components/ui/input';
import Label from '../../components/ui/label';
import { FiAlertCircle, FiArrowLeft, FiCheck, FiChevronDown, FiUploadCloud, FiFile, FiImage, FiXCircle } from 'react-icons/fi';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { format } from 'date-fns';
import MapboxPlacesAutocomplete from '../../components/ui/mapbox-places-autocomplete';
import { parseFieldOptions } from '../../utils/formHelpers';
import ImageLightbox from '../../components/ui/ImageLightbox';
import ImageOptionCard from '../../components/forms/ImageOptionCard';

// Image rendering is now data-driven via field.appearance.enableImages and option.imageUrl

// Progress Indicator Component - elegant minimal design
const FormProgressIndicator = ({ completedFields, totalRequiredFields, className = '' }) => {
	const percentage = totalRequiredFields > 0
		? Math.round((completedFields / totalRequiredFields) * 100)
		: 0;

	return (
		<div className={`${className}`}>
			<div className='mt-6 pt-6 border-t border-gray-100'>
				<div className='flex items-center justify-between mb-3'>
					<span className='text-xs font-montserrat-medium text-gray-400 uppercase tracking-[0.1em]'>
						Progress
					</span>
					<span className='text-xs font-montserrat-medium text-gray-400 tabular-nums'>
						{percentage}%
					</span>
				</div>
				<div className='w-full bg-gray-200/60 rounded-full h-1 overflow-hidden'>
					<div
						className='h-1 rounded-full transition-all duration-700 ease-out bg-gradient-to-r from-[#CC1DB9] to-[#CC1DB9]/80'
						style={{ width: `${percentage}%` }}
					></div>
				</div>
			</div>
		</div>
	);
};

// Custom styled Select component with luxury styling (matching FormRenderer)
const StyledSelect = ({
	id,
	name,
	value,
	onChange,
	options,
	className,
	error,
	...props
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const selectRef = useRef(null);

	// Find selected option label
	const selectedOption = options.find(opt => (opt.value || opt) === value);
	const selectedLabel = selectedOption ? (selectedOption.label || selectedOption) : '';

	// Close dropdown when clicking outside
	useEffect(() => {
		const handleClickOutside = (event) => {
			if (selectRef.current && !selectRef.current.contains(event.target)) {
				setIsOpen(false);
			}
		};
		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, []);

	return (
		<div ref={selectRef} className="relative">
			<button
				type="button"
				id={id}
				onClick={() => setIsOpen(!isOpen)}
				className={`w-full px-4 py-3 text-left bg-white border ${
					error ? 'border-red-400' : isOpen ? 'border-[#CC1DB9]' : 'border-gray-300'
				} rounded-lg font-montserrat-regular text-gray-900
				focus:outline-none focus:ring-2 focus:ring-[#CC1DB9]/40 focus:border-[#CC1DB9]
				transition-[border-color,box-shadow,background-color] duration-300 ease-in-out
				hover:border-gray-400 flex items-center justify-between`}
				{...props}
			>
				<span className={value ? 'text-gray-900' : 'text-gray-400'}>
					{selectedLabel || 'Select an option'}
				</span>
				<FiChevronDown className={`h-5 w-5 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
			</button>

			{isOpen && (
				<div className="absolute z-50 w-full mt-2 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
					<div className="max-h-60 overflow-y-auto">
						{options.map((option, index) => {
							const optionValue = option.value || option;
							const optionLabel = option.label || option;
							const isSelected = optionValue === value;

							return (
								<button
									key={index}
									type="button"
									onClick={() => {
										onChange(optionValue);
										setIsOpen(false);
									}}
									className={`w-full px-4 py-3 text-left font-montserrat-regular transition-colors duration-150
										${isSelected
											? 'bg-gray-900 text-white'
											: 'text-gray-700 hover:bg-gray-50'
										}
										${index !== options.length - 1 ? 'border-b border-gray-100' : ''}
									`}
								>
									<div className="flex items-center justify-between">
										<span>{optionLabel}</span>
										{isSelected && <FiCheck className="h-4 w-4" />}
									</div>
								</button>
							);
						})}
					</div>
				</div>
			)}

			{/* Hidden native select for form submission */}
			<select
				name={name}
				value={value}
				onChange={(e) => onChange(e.target.value)}
				className="sr-only"
				tabIndex={-1}
			>
				<option value=''>Select an option</option>
				{options.map((option, index) => (
					<option key={index} value={option.value || option}>
						{option.label || option}
					</option>
				))}
			</select>
		</div>
	);
};

const FormPreview = ({
	isPreview = false,
	form: propForm,
	sections: propSections,
	fields: propFields,
	fieldSectionMap: propFieldSectionMap,
	onBack,
}) => {
	const { id } = useParams();
	const history = useHistory();
	const dispatch = useDispatch();
	const {
		currentForm: reduxForm,
		formFields: reduxFormFields,
		formSections: reduxFormSections,
		fieldSectionMap: reduxFieldSectionMap,
		status,
		error,
	} = useSelector((state) => state.formBuilder);
	const token = useSelector((state) => state.admin.token);
	const [formData, setFormData] = useState({});
	const [errors, setErrors] = useState({});
	const [currentSection, setCurrentSection] = useState(0);
	const [validationModal, setValidationModal] = useState({
		show: false,
		message: '',
	});
	const [lightboxImage, setLightboxImage] = useState(null);

	// Use props if provided, otherwise fall back to Redux state
	const form = propForm || reduxForm;
	const sections = propSections || reduxFormSections || [];
	const fields = propFields || reduxFormFields || [];
	const fieldSectionMap = propFieldSectionMap || reduxFieldSectionMap || [];

	// Normalize header image key
	const headerImage = form?.headerImage || form?.header_image || '';

	// Calculate form progress based on required fields - must be before early returns
	const { completedFields, totalRequiredFields } = useMemo(() => {
		const requiredFields = fields?.filter(f => {
			const fieldType = f.type?.toLowerCase();
			// Skip display-only fields
			if (['heading', 'paragraph', 'divider'].includes(fieldType)) return false;
			return f.is_required;
		}) || [];

		const completed = requiredFields.filter(f => {
			const value = formData[f.field_key];
			if (Array.isArray(value)) return value.length > 0;
			if (typeof value === 'object' && value !== null) {
				// For address fields, check if street is filled
				return value.street && value.street.trim() !== '';
			}
			return value && value.toString().trim() !== '';
		}).length;

		return { completedFields: completed, totalRequiredFields: requiredFields.length };
	}, [fields, formData]);

	useEffect(() => {
		if (!propForm && id) {
			dispatch(getFormById({ id, token }));
		}

		// Comment out automatic creation of Other Information section
		/*
		// Create Other Information section if it doesn't exist
		if (
			form &&
			!sections.some((section) => section.title === 'Other Information')
		) {
			dispatch(
				createFormSection({
					formId: form.id,
					sectionData: {
						title: 'Other Information',
						description: 'Additional details and contact information',
						position: sections.length,
						form_id: form.id,
					},
					token,
				})
			).then((response) => {
				// After section is created, assign phone field to it
				const otherInfoSection = response.payload;
				const phoneField = fields.find(
					(field) => field.type === 'tel' || field.field_key === 'phone'
				);

				if (phoneField && otherInfoSection) {
					dispatch(
						assignFieldToSection({
							formId: form.id,
							fieldId: phoneField.id,
							sectionId: otherInfoSection.id,
							position: 0,
							token,
						})
					);
				}
			});
		}
		*/
	}, [id, dispatch, token, propForm]);

	useEffect(() => {
		if (fields?.length > 0) {
			const initialData = {};
			fields.forEach((field) => {
				// Parse appearance if it's a string
				let appearance = field.appearance;
				if (typeof appearance === 'string' && appearance) {
					try {
						appearance = JSON.parse(appearance);
					} catch (e) {
						console.error('Error parsing appearance for default values:', e);
						appearance = {};
					}
				} else {
					appearance = appearance || {};
				}

				const fieldType = field.type?.toLowerCase();

				// Skip display-only fields that don't need form data
				if (['heading', 'paragraph', 'divider'].includes(fieldType)) {
					return;
				}

				if (appearance?.defaultValue) {
					initialData[field.field_key] = appearance.defaultValue;
				} else {
					switch (fieldType) {
						case 'checkbox':
						case 'multiselect':
							initialData[field.field_key] = [];
							break;
						case 'number':
						case 'price':
							initialData[field.field_key] = '';
							break;
						case 'address':
							initialData[field.field_key] = {
								street: '',
								city: '',
								state: '',
								zip: '',
							};
							break;
						default:
							initialData[field.field_key] = '';
					}
				}
			});
			setFormData(initialData);
		}
	}, [fields]);

	if (status === 'loading' && !propForm) {
		return (
			<div className='flex justify-center items-center h-64'>
				<div className='animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-900'></div>
			</div>
		);
	}

	if ((status === 'failed' || !form) && !propForm) {
		return (
			<div className='p-4'>
				<div className='bg-white rounded-xl shadow-sm border border-red-100 p-6'>
					<h2 className='text-xl font-glacial font-semibold text-gray-900 mb-2'>Error Loading Form</h2>
					<p className='text-sm font-montserrat-regular text-red-600'>{error || 'Failed to load form'}</p>
				</div>
			</div>
		);
	}

	const validateField = (field, value) => {
		const rules = field.validation_rules;
		const fieldType = field.type?.toLowerCase();

		// Skip validation for display-only fields
		if (['heading', 'paragraph', 'divider'].includes(fieldType)) {
			return '';
		}

		if (!rules) return '';

		if (field.is_required && (!value || value.length === 0)) {
			return field.validation_rules.errorMessage || 'This field is required';
		}

		if (!value) return ''; // Skip other validations if field is empty and not required

		switch (fieldType) {
			case 'text':
			case 'textarea':
				if (rules.minLength && value.length < rules.minLength) {
					return `Minimum length is ${rules.minLength} characters`;
				}
				if (rules.maxLength && value.length > rules.maxLength) {
					return `Maximum length is ${rules.maxLength} characters`;
				}
				if (rules.pattern && !new RegExp(rules.pattern).test(value)) {
					return rules.errorMessage || 'Invalid format';
				}
				break;

			case 'number':
			case 'price':
				const num = Number(value);
				if (isNaN(num)) return 'Please enter a valid number';
				if (rules.numberRange?.min && num < rules.numberRange.min) {
					return `Minimum value is ${rules.numberRange.min}`;
				}
				if (rules.numberRange?.max && num > rules.numberRange.max) {
					return `Maximum value is ${rules.numberRange.max}`;
				}
				break;

			case 'email':
				if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
					return 'Please enter a valid email address';
				}
				break;

			case 'phone':
			case 'tel':
				// Basic phone validation - allow numbers, spaces, dashes, parentheses, plus signs, and dots
				if (!/^[0-9\s\-\(\)\+\.]+$/.test(value)) {
					return 'Please enter a valid phone number';
				}
				break;

			case 'url':
				try {
					const urlToTest = /^https?:\/\//i.test(value) ? value : `https://${value}`;
					new URL(urlToTest);
				} catch {
					return 'Please enter a valid URL';
				}
				break;

			case 'date':
				if (
					rules.dateRange?.min &&
					new Date(value) < new Date(rules.dateRange.min)
				) {
					return `Date must be after ${format(
						new Date(rules.dateRange.min),
						'PP'
					)}`;
				}
				if (
					rules.dateRange?.max &&
					new Date(value) > new Date(rules.dateRange.max)
				) {
					return `Date must be before ${format(
						new Date(rules.dateRange.max),
						'PP'
					)}`;
				}
				break;

			case 'file':
			case 'image':
				if (value instanceof FileList) {
					const file = value[0];
					if (
						rules.maxFileSize &&
						file.size > rules.maxFileSize * 1024 * 1024
					) {
						return `File size must be less than ${rules.maxFileSize}MB`;
					}
					if (rules.fileTypes?.length > 0) {
						const ext = '.' + file.name.split('.').pop().toLowerCase();
						if (!rules.fileTypes.includes(ext)) {
							return `Allowed file types: ${rules.fileTypes.join(', ')}`;
						}
					}
				}
				break;
		}

		return '';
	};

	const handleInputChange = (field, value) => {
		setFormData((prev) => ({
			...prev,
			[field.field_key]: value,
		}));

		const error = validateField(field, value);
		setErrors((prev) => ({
			...prev,
			[field.field_key]: error,
		}));
	};

	const handleSubmit = (e) => {
		e.preventDefault();

		// Validate all fields
		const newErrors = {};
		const missingRequiredFields = [];
		const invalidFields = [];
		let hasErrors = false;

		fields.forEach((field) => {
			const fieldType = field.type?.toLowerCase();

			// Skip validation for display-only fields
			if (['heading', 'paragraph', 'divider'].includes(fieldType)) {
				return;
			}

			const fieldValue = formData[field.field_key];
			const error = validateField(field, fieldValue);

			if (error) {
				newErrors[field.field_key] = error;
				hasErrors = true;

				// Categorize errors
				if (field.is_required && (!fieldValue || fieldValue.length === 0)) {
					missingRequiredFields.push(field.label);
				} else {
					invalidFields.push(`${field.label}: ${error}`);
				}
			}
		});

		setErrors(newErrors);

		if (hasErrors) {
			// Only show popup for multiple errors (3+), otherwise let inline validation handle it
			if (missingRequiredFields.length + invalidFields.length >= 3) {
				// Create a comprehensive error message
				let modalMessage = '';

				if (missingRequiredFields.length > 0) {
					modalMessage += `Please fill in the following required fields: ${missingRequiredFields.join(
						', '
					)}`;
				}

				if (invalidFields.length > 0) {
					if (modalMessage) modalMessage += '\n\n';
					modalMessage += `Please fix the following validation errors:\n${invalidFields.join(
						'\n'
					)}`;
				}

				setValidationModal({
					show: true,
					message: modalMessage,
				});
			}
			return;
		}

		// Form is valid, proceed with submission
		// Implement actual form submission logic here
	};

	// Get fields for a section
	const getFieldsForSection = (sectionId) => {
		if (!fields) return [];
		// Just filter by section_id since fields are already ordered by global position from Redux
		return fields.filter((field) => {
			return fieldSectionMap.some(
				(mapping) =>
					mapping.section_id === sectionId && mapping.field_id === field.id
			);
		});
	};

	// Get unassigned fields (not in any section)
	const getUnassignedFields = () => {
		if (!fields) return [];
		// Return fields that don't have a mapping in fieldSectionMap
		return fields.filter((field) => {
			return !fieldSectionMap.some((mapping) => mapping.field_id === field.id);
		});
	};

	// Render a single field based on its type
	const renderField = (field) => {
		const fieldValue = formData[field.field_key] || '';
		const fieldError = errors[field.field_key];

		// Parse appearance data if it's a string
		let appearance = field.appearance;

		if (typeof appearance === 'string' && appearance) {
			try {
				appearance = JSON.parse(appearance);
			} catch (e) {
				console.error('Error parsing appearance in preview:', e);
				appearance = {};
			}
		} else {
			appearance = appearance || {};
		}

		// Enhanced luxury field wrapper with sophisticated typography and spacing (matching FormRenderer)
		const fieldWrapper = (children, options = {}) => (
			<div className='group'>
				<Label
					htmlFor={field.field_key}
					className='block text-xs font-montserrat-medium text-gray-500 mb-3 uppercase tracking-[0.1em] transition-colors duration-200 group-focus-within:text-black'>
					{field.label}
					{field.is_required && <span className='text-[#CC1DB9] ml-1 text-[11px] align-super font-montserrat-medium'>*</span>}
				</Label>
				<div className='mt-2'>{children}</div>
				{field.description && (
					<p className='text-sm font-montserrat-regular text-gray-500 mt-2 leading-relaxed'>
						{field.description}
					</p>
				)}
				{/* Character count for textarea fields */}
				{options.showCharCount && (
					<div className='mt-2 flex justify-end'>
						<span className={`text-xs font-montserrat-regular ${
							options.charCount > (options.maxLength || 500)
								? 'text-red-500'
								: 'text-gray-400'
						}`}>
							{options.charCount || 0}{options.maxLength ? ` / ${options.maxLength}` : ''} characters
						</span>
					</div>
				)}
				{fieldError && (
					<div role='alert' aria-live='polite' className='mt-2 px-0 py-1'>
						<p className='text-xs text-red-500 font-montserrat-regular'>
							{fieldError}
						</p>
					</div>
				)}
			</div>
		);

		// Size class based on appearance
		const sizeClass =
			appearance.size === 'small'
				? 'text-sm py-2'
				: appearance.size === 'large'
				? 'text-base py-4'
				: 'py-3';

		// Full width for form fields
		const widthClass = 'w-full';

		// Enhanced input base classes with luxury styling and smooth transitions (matching FormRenderer)
		const inputClasses = `${widthClass} ${sizeClass} px-4 border ${
			fieldError ? 'border-red-400 bg-red-50/30' : 'border-gray-300 bg-white'
		} rounded-lg
		font-montserrat-regular text-gray-900 placeholder-gray-500
		focus:outline-none focus:ring-2 focus:ring-[#CC1DB9]/40 focus:border-[#CC1DB9] focus:bg-white
		hover:border-gray-400
		transition-[border-color,box-shadow,background-color] duration-300 ease-in-out`;

		const fieldType = field.type?.toLowerCase();

		switch (fieldType) {
			case 'text':
				return fieldWrapper(
					<Input
						id={field.field_key}
						name={field.field_key}
						value={fieldValue}
						onChange={(e) => handleInputChange(field, e.target.value)}
						placeholder={field.placeholder}
						required={field.is_required}
						data-size={appearance.size}
						className={inputClasses}
					/>
				);

			case 'textarea':
				const maxLength = field.validation_rules?.maxLength;
				return fieldWrapper(
					<Textarea
						id={field.field_key}
						name={field.field_key}
						value={fieldValue}
						onChange={(e) => handleInputChange(field, e.target.value)}
						placeholder={field.placeholder}
						required={field.is_required}
						data-size={appearance.size}
						className={inputClasses}
					/>,
					{
						showCharCount: true,
						charCount: fieldValue?.length || 0,
						maxLength: maxLength
					}
				);

			case 'number':
				return fieldWrapper(
					<Input
						id={field.field_key}
						name={field.field_key}
						value={fieldValue}
						onChange={(e) => handleInputChange(field, e.target.value)}
						type='number'
						placeholder={field.placeholder}
						required={field.is_required}
						data-size={appearance.size}
						className={inputClasses}
					/>
				);

			case 'email':
				return fieldWrapper(
					<Input
						id={field.field_key}
						name={field.field_key}
						value={fieldValue}
						onChange={(e) => handleInputChange(field, e.target.value)}
						type='email'
						placeholder={field.placeholder}
						required={field.is_required}
						data-size={appearance.size}
						className={inputClasses}
					/>
				);

			case 'phone':
			case 'tel':
				return fieldWrapper(
					<Input
						id={field.field_key}
						name={field.field_key}
						value={fieldValue}
						onChange={(e) => handleInputChange(field, e.target.value)}
						type='tel'
						placeholder={field.placeholder}
						required={field.is_required}
						pattern='[0-9\s\-\(\)\+\.]+'
						title='Please enter a valid phone number'
						data-size={appearance.size}
						className={inputClasses}
					/>
				);

			case 'url':
				return fieldWrapper(
					<Input
						id={field.field_key}
						name={field.field_key}
						value={fieldValue}
						onChange={(e) => handleInputChange(field, e.target.value)}
						onBlur={(e) => {
							const v = e.target.value.trim();
							if (v && !/^https?:\/\//i.test(v)) {
								handleInputChange(field, `https://${v}`);
							}
						}}
						type='text'
						placeholder={field.placeholder || 'e.g. example.com'}
						required={field.is_required}
						data-size={appearance.size}
						className={inputClasses}
					/>
				);

			case 'date':
				return fieldWrapper(
					<Input
						id={field.field_key}
						name={field.field_key}
						value={fieldValue}
						onChange={(e) => handleInputChange(field, e.target.value)}
						type='date'
						placeholder={field.placeholder}
						required={field.is_required}
						data-size={appearance.size}
						className={inputClasses}
					/>
				);

			case 'time':
				return fieldWrapper(
					<Input
						id={field.field_key}
						name={field.field_key}
						value={fieldValue}
						onChange={(e) => handleInputChange(field, e.target.value)}
						type='time'
						placeholder={field.placeholder}
						required={field.is_required}
						data-size={appearance.size}
						className={inputClasses}
					/>
				);

			case 'datetime':
				return fieldWrapper(
					<Input
						id={field.field_key}
						name={field.field_key}
						value={fieldValue}
						onChange={(e) => handleInputChange(field, e.target.value)}
						type='datetime-local'
						placeholder={field.placeholder}
						required={field.is_required}
						data-size={appearance.size}
						className={inputClasses}
					/>
				);

			case 'select': {
				const selectOptions = parseFieldOptions(field.options);

				if (selectOptions.length > 0) {
					return fieldWrapper(
						<StyledSelect
							id={field.field_key}
							name={field.field_key}
							value={fieldValue}
							onChange={(value) => handleInputChange(field, value)}
							options={selectOptions}
							error={fieldError}
						/>
					);
				}
				// Fallback to text input when no options are available
				return fieldWrapper(
					<Input
						id={field.field_key}
						name={field.field_key}
						value={fieldValue}
						onChange={(e) => handleInputChange(field, e.target.value)}
						placeholder={field.placeholder || 'Enter value'}
						required={field.is_required}
						className={inputClasses}
					/>
				);
			}

			case 'radio': {
				const radioOptions = parseFieldOptions(field.options);
				const radioHasImages = appearance?.enableImages && radioOptions.some(opt => opt.imageUrl);

				if (radioHasImages) {
					return fieldWrapper(
						<RadioGroup
							id={field.field_key}
							name={field.field_key}
							value={fieldValue}
							onValueChange={(value) => handleInputChange(field, value)}
							className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4'>
							{radioOptions.map((option) => {
								const optionValue = option.value || option;
								const isSelected = fieldValue === optionValue;
								return (
									<ImageOptionCard
										key={optionValue}
										option={option}
										fieldKey={field.field_key}
										isSelected={isSelected}
										variant='radio'
										onImageClick={setLightboxImage}
									>
										<RadioGroupItem
											value={optionValue}
											id={`${field.field_key}-${optionValue}`}
											className='sr-only'
										/>
									</ImageOptionCard>
								);
							})}
						</RadioGroup>
					);
				}

				if (radioOptions.length > 0) {
					return fieldWrapper(
						<RadioGroup
							id={field.field_key}
							name={field.field_key}
							value={fieldValue}
							onValueChange={(value) => handleInputChange(field, value)}
							className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
							{radioOptions.map((option) => {
								const optionValue = option.value || option;
								const optionLabel = option.label || option;
								const isSelected = fieldValue === optionValue;
								return (
									<label
										key={optionValue}
										htmlFor={`${field.field_key}-${optionValue}`}
										className={`relative flex items-center p-4 rounded-lg border cursor-pointer transition-all duration-300 ease-in-out
											${isSelected
												? 'border-black bg-gray-50/50'
												: 'border-gray-200 bg-white hover:border-gray-400'
											}
										`}
									>
										<RadioGroupItem
											value={optionValue}
											id={`${field.field_key}-${optionValue}`}
											className='sr-only'
										/>
										<div className={`flex-shrink-0 w-4 h-4 rounded-full border-2 mr-3 flex items-center justify-center transition-all duration-200
											${isSelected ? 'border-gray-900 bg-gray-900' : 'border-gray-300 bg-white'}
										`}>
											{isSelected && (
												<div className='w-1.5 h-1.5 rounded-full bg-white' />
											)}
										</div>
										<span className={`font-montserrat-regular text-sm ${isSelected ? 'text-black font-montserrat-medium' : 'text-gray-600'}`}>
											{optionLabel}
										</span>
									</label>
								);
							})}
						</RadioGroup>
					);
				}
				return fieldWrapper(
					<Input
						id={field.field_key}
						name={field.field_key}
						value={fieldValue}
						onChange={(e) => handleInputChange(field, e.target.value)}
						placeholder={field.placeholder}
						required={field.is_required}
						className={inputClasses}
					/>
				);
			}

			case 'multiselect':
			case 'checkbox': {
				const checkboxOptions = parseFieldOptions(field.options);
				const checkboxHasImages = appearance?.enableImages && checkboxOptions.some(opt => opt.imageUrl);

				if (checkboxHasImages) {
					const selectedValues = Array.isArray(fieldValue) ? fieldValue : [];
					return fieldWrapper(
						<div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4'>
							{checkboxOptions.map((option) => {
								const optionValue = option.value || option;
								const isSelected = selectedValues.includes(optionValue);
								return (
									<ImageOptionCard
										key={optionValue}
										option={option}
										fieldKey={field.field_key}
										isSelected={isSelected}
										variant='checkbox'
										onImageClick={setLightboxImage}
									>
										<Checkbox
											id={`${field.field_key}-${optionValue}`}
											name={field.field_key}
											checked={isSelected}
											onCheckedChange={(isChecked) => {
												let newValues;
												if (isChecked) {
													newValues = [...selectedValues, optionValue];
												} else {
													newValues = selectedValues.filter((v) => v !== optionValue);
												}
												handleInputChange(field, newValues);
											}}
											className='sr-only'
										/>
									</ImageOptionCard>
								);
							})}
						</div>
					);
				}

				if (checkboxOptions.length > 0) {
					const selectedValues = Array.isArray(fieldValue) ? fieldValue : [];
					return fieldWrapper(
						<div className='grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-80 overflow-y-auto p-1'>
							{checkboxOptions.map((option) => {
								const optionValue = option.value || option;
								const optionLabel = option.label || option;
								const isSelected = selectedValues.includes(optionValue);
								return (
									<label
										key={optionValue}
										htmlFor={`${field.field_key}-${optionValue}`}
										className={`relative flex items-center p-4 rounded-lg border cursor-pointer transition-all duration-300 ease-in-out
											${isSelected
												? 'border-black bg-gray-50/50'
												: 'border-gray-200 bg-white hover:border-gray-400'
											}
										`}
									>
										<Checkbox
											id={`${field.field_key}-${optionValue}`}
											name={field.field_key}
											checked={isSelected}
											onCheckedChange={(isChecked) => {
												let newValues;
												if (isChecked) {
													newValues = [...selectedValues, optionValue];
												} else {
													newValues = selectedValues.filter(
														(v) => v !== optionValue
													);
												}
												handleInputChange(field, newValues);
											}}
											className='sr-only'
										/>
										<div className={`flex-shrink-0 w-4 h-4 rounded-sm border-2 mr-3 flex items-center justify-center transition-all duration-200
											${isSelected ? 'border-gray-900 bg-gray-900' : 'border-gray-300 bg-white'}
										`}>
											{isSelected && (
												<FiCheck className='h-2.5 w-2.5 text-white' />
											)}
										</div>
										<span className={`font-montserrat-regular text-sm ${isSelected ? 'text-black font-montserrat-medium' : 'text-gray-600'}`}>
											{optionLabel}
										</span>
									</label>
								);
							})}
						</div>
					);
				}
				return fieldWrapper(
					<Input
						id={field.field_key}
						name={field.field_key}
						value={fieldValue}
						onChange={(e) => handleInputChange(field, e.target.value)}
						placeholder='Enter values (comma separated)'
						required={field.is_required}
						className={inputClasses}
					/>
				);
			}

			case 'heading':
				// Display element - no input needed
				return (
					<div className='py-4 mb-2'>
						<h3 className='text-base font-montserrat-medium text-black uppercase tracking-[0.15em]'>
							{field.label}
						</h3>
						{field.description && (
							<p className='text-sm font-montserrat-regular text-gray-500 mt-2 leading-relaxed'>{field.description}</p>
						)}
						<div className='mt-3 h-px bg-gradient-to-r from-gray-200 via-gray-200 to-transparent'></div>
					</div>
				);

			case 'paragraph':
				// Display element - no input needed
				return (
					<div className='py-3'>
						<div className='text-sm font-montserrat-regular text-gray-600 leading-relaxed'>
							{field.description || field.label}
						</div>
					</div>
				);

			case 'divider':
				// Visual divider - no input needed
				return (
					<div className='py-6'>
						<div className='relative'>
							<div className='h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent'></div>
							{field.label && field.label !== 'Section Divider' && (
								<div className='absolute inset-0 flex items-center justify-center'>
									<span className='bg-white px-4 text-xs font-montserrat-medium text-gray-400 uppercase tracking-wider'>
										{field.label}
									</span>
								</div>
							)}
						</div>
					</div>
				);

			case 'address':
				return fieldWrapper(
					<MapboxPlacesAutocomplete
						id={field.field_key}
						name={field.field_key}
						value={fieldValue}
						onChange={(address) => {
							handleInputChange(field, {
								street: address.street || '',
								city: address.city || '',
								state: address.state || '',
								zip: address.zip || '',
								coordinates: address.coordinates || null,
								formatted_address: address.formatted_address || '',
							});
						}}
						placeholder={field.placeholder || 'Enter an address'}
						required={field.is_required}
						error={fieldError}
						disabled={isPreview}
						className={widthClass}
					/>
				);

			case 'file':
			case 'image':
				// Elegant drag-drop upload interface (matching FormRenderer)
				// Using CSS classes for drag state since we can't use useState in switch case
				const isImageField = fieldType === 'image';

				const handleDragOver = (e) => {
					e.preventDefault();
					e.stopPropagation();
					if (!isPreview) {
						const dropZone = e.currentTarget.querySelector('.drop-zone');
						const iconZone = e.currentTarget.querySelector('.icon-zone');
						const textZone = e.currentTarget.querySelector('.text-zone');
						if (dropZone) {
							dropZone.classList.add('border-gray-900', 'bg-gray-50', 'shadow-lg');
							dropZone.classList.remove('border-gray-300', 'bg-white');
						}
						if (iconZone) {
							iconZone.classList.add('bg-gray-900', 'text-white', 'scale-110');
							iconZone.classList.remove(isImageField ? 'bg-pink-50' : 'bg-gray-100', isImageField ? 'text-primary' : 'text-gray-400');
						}
						if (textZone) {
							textZone.textContent = isImageField ? 'Drop your image here' : 'Drop your file here';
						}
					}
				};

				const handleDragLeave = (e) => {
					e.preventDefault();
					e.stopPropagation();
					const dropZone = e.currentTarget.querySelector('.drop-zone');
					const iconZone = e.currentTarget.querySelector('.icon-zone');
					const textZone = e.currentTarget.querySelector('.text-zone');
					if (dropZone) {
						dropZone.classList.remove('border-gray-900', 'bg-gray-50', 'shadow-lg');
						dropZone.classList.add('border-gray-300', 'bg-white');
					}
					if (iconZone) {
						iconZone.classList.remove('bg-gray-900', 'text-white', 'scale-110');
						iconZone.classList.add(isImageField ? 'bg-pink-50' : 'bg-gray-100', isImageField ? 'text-primary' : 'text-gray-400');
					}
					if (textZone) {
						textZone.textContent = isPreview
							? (isImageField ? 'Image upload field' : 'File upload field')
							: (isImageField ? 'Drag and drop your image here' : 'Drag and drop your file here');
					}
				};

				const handleDrop = (e) => {
					e.preventDefault();
					e.stopPropagation();
					handleDragLeave(e);
					if (!isPreview && e.dataTransfer.files?.length > 0) {
						handleInputChange(field, e.dataTransfer.files);
					}
				};

				return fieldWrapper(
					<div className='space-y-4'>
						{/* Selected file preview */}
						{fieldValue && fieldValue[0] && (
							<div className={`flex items-center justify-between p-4 bg-white rounded-lg border shadow-sm transition-all duration-200 hover:shadow-md ${
								isImageField ? 'border-pink-100' : 'border-gray-200'
							}`}>
								<div className='flex items-center space-x-4'>
									{isImageField ? (
										<div className='w-16 h-16 rounded-lg bg-pink-50 border border-pink-100 flex items-center justify-center'>
											<FiImage className='h-8 w-8 text-primary' />
										</div>
									) : (
										<div className='w-16 h-16 rounded-lg bg-gray-100 border border-gray-200 flex items-center justify-center'>
											<FiFile className='h-8 w-8 text-gray-500' />
										</div>
									)}
									<div>
										<div className='text-sm font-montserrat-medium text-gray-900 mb-1'>
											{fieldValue[0].name}
										</div>
										<div className='text-xs font-montserrat-regular text-gray-500'>
											{(fieldValue[0].size / 1024 / 1024).toFixed(2)} MB
										</div>
									</div>
								</div>
								<button
									type='button'
									onClick={() => handleInputChange(field, '')}
									className='p-2 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-all duration-200'
									title='Remove file'>
									<FiXCircle className='h-5 w-5' />
								</button>
							</div>
						)}

						{/* Elegant drag-drop upload interface with visual distinction */}
						{!fieldValue && (
							<label
								htmlFor={`file-${field.field_key}`}
								className={`relative block cursor-pointer transition-all duration-300 ease-in-out
									${isPreview ? 'pointer-events-none opacity-75' : ''}
								`}
								onDragOver={handleDragOver}
								onDragEnter={handleDragOver}
								onDragLeave={handleDragLeave}
								onDrop={handleDrop}
							>
								<div className={`drop-zone border-2 border-dashed rounded-xl p-8 text-center transition-all duration-300 border-gray-300 bg-white hover:border-gray-400 hover:bg-gray-50/50 hover:shadow-sm ${
									isImageField ? 'hover:border-pink-200' : ''
								}`}>
									<div className={`icon-zone mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4 transition-all duration-300 ${
										isImageField ? 'bg-pink-50 text-primary' : 'bg-gray-100 text-gray-400'
									}`}>
										{isImageField ? <FiImage className='h-8 w-8' /> : <FiUploadCloud className='h-8 w-8' />}
									</div>
									<div className='space-y-2'>
										<p className='text-zone text-base font-montserrat-medium text-gray-900'>
											{isPreview
												? (isImageField ? 'Image upload field' : 'File upload field')
												: (isImageField ? 'Drag and drop your image here' : 'Drag and drop your file here')}
										</p>
										{!isPreview && (
											<p className='text-sm font-montserrat-regular text-gray-500'>
												or <span className={`underline underline-offset-2 ${isImageField ? 'text-primary' : 'text-gray-900'}`}>browse</span> to choose {isImageField ? 'an image' : 'a file'}
											</p>
										)}
									</div>
									<div className='mt-4 pt-4 border-t border-gray-100'>
										<p className={`text-xs font-montserrat-medium ${isImageField ? 'text-primary/70' : 'text-gray-500'}`}>
											{field.validation_rules?.fileTypes?.length > 0
												? `Accepted: ${field.validation_rules.fileTypes.join(', ')}`
												: (isImageField ? 'Accepted: JPG, PNG, GIF, WebP' : 'All file types accepted')
											}
											{field.validation_rules?.maxFileSize && (
												<span className='text-gray-400'> | Max: {field.validation_rules.maxFileSize}MB</span>
											)}
										</p>
									</div>
								</div>
								<input
									id={`file-${field.field_key}`}
									type='file'
									className='sr-only'
									onChange={(e) => {
										if (e.target.files?.length > 0) {
											handleInputChange(field, e.target.files);
										}
									}}
									accept={
										field.validation_rules?.fileTypes?.length > 0
											? field.validation_rules.fileTypes.join(',')
											: (isImageField ? 'image/*' : '*')
									}
									disabled={isPreview}
								/>
							</label>
						)}

						{/* Replace file option when file exists */}
						{fieldValue && fieldValue[0] && !isPreview && (
							<label
								htmlFor={`file-${field.field_key}`}
								className='block cursor-pointer'
							>
								<div className={`border rounded-lg p-3 text-center transition-all duration-200 hover:shadow-sm ${
									isImageField ? 'border-pink-100 hover:bg-pink-50/50' : 'border-gray-200 hover:bg-gray-50'
								}`}>
									<p className={`text-sm font-montserrat-medium ${isImageField ? 'text-primary' : 'text-gray-600'}`}>
										{isImageField ? <FiImage className='inline-block h-4 w-4 mr-2 -mt-0.5' /> : <FiUploadCloud className='inline-block h-4 w-4 mr-2 -mt-0.5' />}
										Replace with a different {isImageField ? 'image' : 'file'}
									</p>
								</div>
								<input
									id={`file-${field.field_key}`}
									type='file'
									className='sr-only'
									onChange={(e) => {
										if (e.target.files?.length > 0) {
											handleInputChange(field, e.target.files);
										}
									}}
									accept={
										field.validation_rules?.fileTypes?.length > 0
											? field.validation_rules.fileTypes.join(',')
											: (isImageField ? 'image/*' : '*')
									}
								/>
							</label>
						)}
					</div>
				);

			default:
				return fieldWrapper(
					<Input
						id={field.field_key}
						name={field.field_key}
						value={fieldValue}
						onChange={(e) => handleInputChange(field, e.target.value)}
						placeholder={field.placeholder}
						required={field.is_required}
						data-size={appearance.size}
						className={inputClasses}
					/>
				);
		}
	};

	// Handler for back navigation
	const handleBack = () => {
		if (onBack) {
			// If onBack prop is provided, use it (for tab navigation in editor)
			onBack();
		} else {
			// Otherwise, navigate back to form builder list (for standalone view route)
			history.push('/admin/form-builder');
		}
	};

	// Check if this is a standalone view (not embedded in editor preview tab)
	const isStandaloneView = !isPreview && id;

	return (
		<div className={isStandaloneView ? 'min-h-screen bg-[#F7F7F5]' : 'p-4 max-w-3xl mx-auto bg-[#F7F7F5] rounded-lg'}>
			<div className={isStandaloneView ? 'max-w-3xl mx-auto px-6 sm:px-8 lg:px-12 py-12 sm:py-16' : ''}>
				{/* Header with back button for standalone view */}
				{isStandaloneView && (
					<div className='mb-8'>
						<button
							onClick={handleBack}
							className='inline-flex items-center px-3 py-2 text-sm font-montserrat-medium text-gray-500 hover:text-gray-900 rounded-lg hover:bg-white hover:shadow-sm transition-all duration-200'
							title='Go back'>
							<FiArrowLeft className='mr-2 h-4 w-4' />
							Back to Forms
						</button>
					</div>
				)}

				{/* Form Header Card with Progress */}
				<div className='bg-white rounded-none sm:rounded-lg overflow-hidden mb-8'>
					{/* Banner Image — data-driven from form.headerImage or form.header_image */}
					{headerImage && (
						<div className='w-full h-40 sm:h-56 lg:h-72 overflow-hidden relative'>
							<img src={headerImage} alt='' className='w-full h-full object-cover brightness-[0.92]' loading='lazy' />
							{/* Cinematic gradient fade into content */}
							<div className='absolute inset-0 bg-gradient-to-t from-white via-white/30 to-transparent' />
							{/* Subtle brand luminescence line */}
							<div className='absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#CC1DB9]/40 to-transparent' />
						</div>
					)}
					<div className={`px-6 sm:px-8 pb-8 relative z-10 ${headerImage ? 'pt-0 -mt-10 sm:-mt-12' : 'pt-8'}`}>
						<h1 className='text-4xl sm:text-5xl font-glacial font-bold text-black tracking-tight leading-tight'>
							{form?.name}
						</h1>
						{form?.description && (
							<p className='text-sm sm:text-base font-montserrat-regular text-gray-500 mt-4 leading-relaxed max-w-xl'>
								{form.description}
							</p>
						)}

						{/* Progress Indicator - show when there are required fields */}
						{totalRequiredFields > 0 && !isPreview && (
							<FormProgressIndicator
								completedFields={completedFields}
								totalRequiredFields={totalRequiredFields}
							/>
						)}
					</div>
				</div>

				<form onSubmit={handleSubmit} className='space-y-10'>
					{sections.length > 0 ? (
						<div className='space-y-10'>
							{sections.map((section, index) => (
								<div
									key={section.id}
									id={`section-${section.id}`}
									className='scroll-mt-20'
								>
									<div className='bg-white rounded-none sm:rounded-lg border-0 sm:border sm:border-gray-100/80 overflow-visible transition-all duration-300'>
										{/* Elegant Section Header */}
										<div className='border-b border-gray-100 px-6 sm:px-8 py-6'>
											<h3 className='text-base font-montserrat-medium text-black uppercase tracking-[0.15em]'>
												{section.title}
											</h3>
											{section.description && (
												<p className='text-sm font-montserrat-regular text-gray-500 mt-2 leading-relaxed'>
													{section.description}
												</p>
											)}
										</div>
										{/* Section Content */}
										<div className='px-6 sm:px-8 py-8'>
											<div className='flex flex-col gap-6'>
												{getFieldsForSection(section.id).map((field) => (
													<div key={field.id}>{renderField(field)}</div>
												))}
											</div>
										</div>
									</div>
								</div>
							))}

							{/* Virtual section for unassigned fields when sections exist */}
							{getUnassignedFields().length > 0 && (
								<div>
									<div className='bg-white rounded-none sm:rounded-lg border-0 sm:border sm:border-gray-100/80 overflow-visible transition-all duration-300'>
										{/* Elegant Section Header */}
										<div className='border-b border-gray-100 px-6 sm:px-8 py-6'>
											<h3 className='text-base font-montserrat-medium text-black uppercase tracking-[0.15em]'>
												Additional Information
											</h3>
										</div>
										{/* Section Content */}
										<div className='px-6 sm:px-8 py-8'>
											<div className='flex flex-col gap-6'>
												{getUnassignedFields().map((field) => (
													<div key={field.id}>{renderField(field)}</div>
												))}
											</div>
										</div>
									</div>
								</div>
							)}
						</div>
					) : (
						// Display unassigned fields directly when no sections exist
						<div className='bg-white rounded-none sm:rounded-lg border-0 sm:border sm:border-gray-100/80 overflow-visible transition-all duration-300'>
							{/* Elegant Section Header */}
							<div className='border-b border-gray-100 px-6 sm:px-8 py-6'>
								<h3 className='text-base font-montserrat-medium text-black uppercase tracking-[0.15em]'>
									Form Fields
								</h3>
							</div>
							{/* Section Content */}
							<div className='px-6 sm:px-8 py-8'>
								<div className='flex flex-col gap-6'>
									{getUnassignedFields().map((field) => (
										<div key={field.id}>{renderField(field)}</div>
									))}
								</div>
							</div>
						</div>
					)}

					{!isPreview && (
						<div className='mt-12 pt-10 border-t border-gray-200 space-y-4'>
							<div className='flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4'>
								<div className='text-xs font-montserrat-regular text-gray-500 uppercase tracking-wider'>
									<span className='text-[#CC1DB9]'>*</span> required
								</div>
								<button
									type='submit'
									className='inline-flex items-center justify-center w-full sm:w-auto px-10 py-3.5 border-0 text-xs font-montserrat-medium rounded-none text-white bg-gradient-to-r from-[#CC1DB9] to-[#CC1DB9]/90 uppercase tracking-[0.15em]
									shadow-lg shadow-[#CC1DB9]/20 hover:shadow-[0_8px_24px_-4px_rgba(204,29,185,0.35)] hover:-translate-y-px
									focus:outline-none focus:ring-1 focus:ring-[#CC1DB9] focus:ring-offset-2
									disabled:opacity-40 disabled:cursor-not-allowed
									active:translate-y-0 active:shadow-[0_2px_8px_-2px_rgba(204,29,185,0.25)] active:scale-[0.98] transition-all duration-150'>
									Submit Form
								</button>
							</div>
						</div>
					)}
				</form>
			</div>

			{/* Validation Modal - Elegant styling matching FormRenderer */}
			{validationModal.show && (
				<div className='fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4'>
					<div className='bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in fade-in zoom-in-95 duration-200'>
						{/* Header */}
						<div className='bg-red-50 px-6 py-5 border-b border-red-100'>
							<div className='flex items-center'>
								<div className='w-10 h-10 rounded-full bg-red-100 flex items-center justify-center mr-4'>
									<FiAlertCircle className='h-5 w-5 text-red-600' />
								</div>
								<h3 className='text-lg font-glacial font-semibold text-gray-900'>
									Please Complete Required Fields
								</h3>
							</div>
						</div>
						{/* Content */}
						<div className='px-6 py-5'>
							<p className='text-sm font-montserrat-regular text-gray-600 whitespace-pre-line leading-relaxed'>
								{validationModal.message}
							</p>
						</div>
						{/* Footer */}
						<div className='px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end'>
							<button
								onClick={() => setValidationModal({ show: false, message: '' })}
								className='inline-flex items-center px-5 py-2.5 text-sm font-montserrat-medium rounded-lg text-white bg-gray-900
								hover:bg-gray-800 shadow-sm
								focus:outline-none focus:ring-2 focus:ring-gray-900/30 focus:ring-offset-2
								transition-all duration-200'>
								Got it
							</button>
						</div>
					</div>
				</div>
			)}

			{/* Image Lightbox */}
			<ImageLightbox image={lightboxImage} onClose={() => setLightboxImage(null)} />
		</div>
	);
};

export default FormPreview;
